import { CSSProperties, useState } from "react";
import {
  GUEST_CANCELLATION_REASONS,
  RESTAURANT_CANCELLATION_REASONS,
  ReservationCancellationReason,
} from "../models/IReservation";
import { changeReservationStatus } from "../services/reservationService";

interface ReservationSummary {
  id: string;
  contactName: string;
  contactPhone: string;
  startsAtUtc: string;
  partySize: number;
}

interface Props {
  reservation: ReservationSummary;
  mode: "reject" | "cancel";
  onClose: () => void;
  onDone: () => void;
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("tr-TR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const noteStyle: CSSProperties = { borderRadius: 10, border: "1.5px solid #e5e7eb" };

export default function ReservationStatusModal({ reservation, mode, onClose, onDone }: Props) {
  const [reason, setReason] = useState<ReservationCancellationReason | "">("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isReject = mode === "reject";
  const status = isReject ? "Rejected" : "Cancelled";
  const title = isReject ? "Rezervasyonu Reddet" : "Rezervasyonu İptal Et";
  const submitLabel = isReject ? "Reddet" : "İptal Et";
  const needsNoteHint = reason === "Other" || reason === "RestaurantOther";

  const handleSubmit = () => {
    if (!reason) {
      setError("Lütfen bir gerekçe seçin.");
      return;
    }
    setSaving(true);
    setError("");
    changeReservationStatus({
      id: reservation.id,
      status,
      cancellationReason: reason,
      cancellationNote: note.trim() || undefined,
    })
      .then(() => {
        onDone();
        onClose();
      })
      .catch((err) => {
        const desc = err?.response?.data?.[0]?.description;
        setError(desc ?? `Rezervasyon ${isReject ? "reddedilemedi" : "iptal edilemedi"}.`);
      })
      .finally(() => setSaving(false));
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 20, padding: "28px 28px 22px", width: "min(96vw, 460px)", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className={`bi ${isReject ? "bi-x-circle" : "bi-calendar-x"}`} style={{ color: "#dc2626", fontSize: "1.05rem" }}></i>
            </div>
            {title}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1.2rem" }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Rezervasyon özeti */}
        <div style={{ background: "#f9fafb", borderRadius: 12, padding: "12px 14px", marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#111827" }}>{reservation.contactName}</div>
          <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 2 }}>
            <i className="bi bi-telephone-fill me-1"></i>{reservation.contactPhone}
            {" · "}
            <i className="bi bi-people-fill me-1"></i>{reservation.partySize} kişi
          </div>
          <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 2 }}>
            <i className="bi bi-calendar-event me-1"></i>{formatDateTime(reservation.startsAtUtc)}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {isReject ? (
            <div>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 8 }}>Gerekçe *</label>
              <ReasonList options={RESTAURANT_CANCELLATION_REASONS} selected={reason} onSelect={setReason} />
            </div>
          ) : (
            <>
              <div>
                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 8 }}>Misafir Gerekçesi</label>
                <ReasonList options={GUEST_CANCELLATION_REASONS} selected={reason} onSelect={setReason} />
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 8 }}>Restoran Gerekçesi</label>
                <ReasonList options={RESTAURANT_CANCELLATION_REASONS} selected={reason} onSelect={setReason} />
              </div>
            </>
          )}

          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
              Not <span style={{ fontWeight: 400, color: "#9ca3af" }}>(opsiyonel{needsNoteHint ? ", detay yazmanız faydalı olur" : ""})</span>
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="form-control"
              style={{ ...noteStyle, resize: "vertical" }}
              placeholder="İsteğe bağlı açıklama..."
            />
          </div>

          {error && (
            <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "8px 12px", color: "#b91c1c", fontSize: "0.82rem" }}>
              <i className="bi bi-exclamation-circle me-1"></i>{error}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
          <button onClick={onClose}
            style={{ padding: "9px 18px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
            Vazgeç
          </button>
          <button onClick={handleSubmit} disabled={saving || !reason}
            style={{
              padding: "9px 22px", borderRadius: 10, border: "none",
              background: saving || !reason ? "#e5e7eb" : "linear-gradient(135deg, #dc2626, #b91c1c)",
              color: saving || !reason ? "#9ca3af" : "#fff", fontWeight: 700, fontSize: "0.85rem",
              cursor: saving || !reason ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 7,
            }}
          >
            {saving
              ? <><span className="spinner-border spinner-border-sm"></span> İşleniyor...</>
              : <><i className={`bi ${isReject ? "bi-x-lg" : "bi-calendar-x"}`}></i> {submitLabel}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

function ReasonList({
  options, selected, onSelect,
}: {
  options: { value: ReservationCancellationReason; label: string }[];
  selected: ReservationCancellationReason | "";
  onSelect: (v: ReservationCancellationReason) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      {options.map((opt) => {
        const isSelected = selected === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelect(opt.value)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
              padding: "9px 12px", borderRadius: 9, textAlign: "left",
              border: `1.5px solid ${isSelected ? "#dc2626" : "#e5e7eb"}`,
              background: isSelected ? "#fef2f2" : "#fff",
              cursor: "pointer", transition: "border-color 0.15s, background 0.15s",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: "0.85rem", color: isSelected ? "#b91c1c" : "#374151" }}>
              {opt.label}
            </span>
            {isSelected && <i className="bi bi-check-circle-fill" style={{ color: "#dc2626", flexShrink: 0 }}></i>}
          </button>
        );
      })}
    </div>
  );
}
